import { Recurrence } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneProps, SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { IQService } from 'angular';
import * as _ from 'underscore';

import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '../../app/AppStore';
import { selectContentTotalLength } from '../../app/selectors/ContentSelectors';
import { hasRunNowPermission } from '../../app/utils/MixedContentHelpers';
import { FeatureFlags } from '../FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { IActionContext } from '../helpers/ActionContext';
import { AlertErrorCode, CommonErrorCode, RemoteAgentErrorCode, SchedulingErrorCode, UnifiedDatasourceErrorCode, WebClientErrorCode } from '../helpers/ErrorCodes';
import { RefreshPropertyHelper } from '../helpers/RefreshPropertyHelper';
import { L10n } from '../L10n/L10n';
import { DatasourceContentType, WorkbookContentType } from '../models/ContentTypes';
import { IContentInfo } from '../models/IContentInfo';
import { ExtractEncryptionState, IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { IRemoteRefreshScheduleInfo } from '../models/IRemoteRefreshScheduleInfo';
import { ITypeaheadSource } from '../models/ITypeaheadSource';
import { IWorkbookBase, IWorkbookInfo } from '../models/IWorkbookInfo';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { IExtractItemInfo, INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataSources } from '../../content/resources/Datasources';
import { RemoteRefreshAgents } from '../resources/RemoteRefreshAgents';
import { RemoteRefreshSchedules } from '../resources/RemoteRefreshSchedules';
import { Workbooks } from '../../content/resources/Workbooks';
import { IGetRemoteRefreshAgentsResult } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { RemoteRefreshTypes } from '../ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { ExtractTaskType } from '../ServerApi/types/IExtractTask';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ISchedule, ISelfServiceScheduleDetails, ScheduleActions } from '../../schedule/types/ISchedule';
import {
  standardActionErrorMsg,
  ActionHelpers, standardActionSuccessMsg, StandardActionToastMessages
} from '../../commonInfra/contentActions/ActionHelpers';
import { ChangeDatasourceRefreshModeAction } from './ChangeDatasourceRefreshModeAction';
import { ChangeDatasourceRemoteAgentAction } from './ChangeDatasourceRemoteAgentAction';
import {
  ChangeDatasourceRemoteRefreshPropertiesAction,
  IRemoteRefreshAgentOption,
  IRemoteRefreshPropertiesEditModel,
} from './ChangeDatasourceRemoteRefreshPropertiesAction';
import { ConfirmActionDialog, IConfirmActionDialogButton } from './ConfirmActionDialog';
import { ContentUrlForItem } from './ContentUrl';
import { ModalService, ModalSize } from './ModalService';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { Messages } from '../../codegen/Localize';
import { RefreshSelfServiceScheduleDialog } from '../../app/content/RefreshSelfServiceScheduleDialog';
import { getRefreshSSSDialogParams } from '../../app/sagas/spaces/extractTasks/CreateExtractRefreshSaga';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { openReactAlertDialog } from '../../commonInfra/dialogs/ReactAlertDialog';
import { ChangeScheduleActionService } from './ChangeScheduleAction';
import {
  IErrorDetail
} from '../../commonInfra/contentActions/ActionMessages';


export enum ExtractActionType {
  createExtracts = 'CreateExtracts',
  encryptExtract = 'EncryptExtract',
  decryptExtract = 'DecryptExtract'
}

export enum CreateExtractTasksMode {
  AllowRefreshNow,
  AllowScheduleOnServer,
  AllowScheduleOnServerAndClient,
  AllowRefreshNowAndOnServer,
  AllowRefreshNowAndOnServerAndClient,
  AllowScheduleOnlyOnClient
}

export interface IExtractTaskTypeOption {
  labelTranslateKey: string;
  value: string;
  tbTestId: string;
}

export interface IRemoteRefreshTypeOption {
  refreshType: string;
}

export interface IExtractTaskSchedule {
  schedule?: ISchedule;
  type?: string;
}

export interface ICreateExtractTasksScope extends ng.IScope {

  dialog: {
    mode: string;   // See DialogMode
    showModeSelector: boolean;
    showRefreshNowTab: boolean;
    showRefreshOnClientTab: boolean;
  };

  extractTask: IExtractTaskSchedule;

  scheduleOnServerPrompt: string;
  scheduleOnClientPrompt: string;
  scheduleSource: ITypeaheadSource;

  refreshNowPrompt: string;

  allowIncremental: boolean;
  extractTaskTypeOptions?: IExtractTaskTypeOption[];

  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;

  // The followings are only for shedules to be run on Bridge Clients
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;
  remoteRefreshPropertiesEditModel: IRemoteRefreshPropertiesEditModel;
  existingRemoteRefreshPropertiesEditModel: IRemoteRefreshPropertiesEditModel;
  remoteAgentOptions: IRemoteRefreshAgentOption[];
  remoteAvailableTypeOptions: string[];
  remoteRefreshTypeOption: IRemoteRefreshTypeOption;

  existingRefreshSchedulesCount: Number;
}

const DialogMode = {
  RefreshNow: 'RefreshNow',
  ScheduleRefreshOnServer: 'ScheduleRefreshOnServer',
  ScheduleRefreshOnClient: 'ScheduleRefreshOnClient'
};

type ExtractType = IWorkbookBase | IDataSourceBase;

export class CreateExtractTasksAction {

  public static $inject = [
    'ConfirmActionDialog',
    '$translate',
    'RemoteRefreshAgents',
    'SearchSuggestionsService',
    'ModalService',
    'scheduleFrequencyDescriptionFilter',
    '$q'
  ];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private RemoteRefreshAgents: RemoteRefreshAgents,
    private SearchSuggestionsService: SearchSuggestionsService,
    private ModalService: ModalService,
    private scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    private $q: IQService
  ) { }

  // Generic logic

  public populateScope(scope: ICreateExtractTasksScope,
                        items: Set<IExtractItemInfo>,
                        scheduleOnServerPrompt: string,
                        scheduleOnClientPrompt: string,
                        allowedMode: CreateExtractTasksMode,
                        refreshNowPrompt: string
  ) {
    scope.extractTask = {
      type: ExtractTaskType.RefreshExtract
    };
    scope.remoteRefreshTypeOption = {
      refreshType: RemoteRefreshTypes.Full
    };

    scope.scheduleOnServerPrompt = scheduleOnServerPrompt;
    scope.scheduleOnClientPrompt = scheduleOnClientPrompt;
    scope.scheduleSource = this.SearchSuggestionsService.schedulesSource(ScheduleActions.Extract);

    scope.allowIncremental = _.any(items.toArray(), (item: IExtractItemInfo) => item.hasIncrementalExtract);
    if (scope.allowIncremental) {
      scope.extractTaskTypeOptions = [
        { value: ExtractTaskType.RefreshExtract,
          labelTranslateKey: 'extractTasksTypeRefreshExtract',
          tbTestId: 'create-extract-refresh-type-full'
        },
        { value: ExtractTaskType.IncrementExtract,
          labelTranslateKey: 'extractTasksTypeIncrementExtract',
          tbTestId: 'create-extract-refresh-type-incremental'
        }
      ];
      scope.remoteAvailableTypeOptions = [
        RemoteRefreshTypes.Full,
        RemoteRefreshTypes.Incremental
      ];
    }

    scope.dialog = {
      mode: (allowedMode === CreateExtractTasksMode.AllowScheduleOnServer ||
             allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient)
             ? DialogMode.ScheduleRefreshOnServer : DialogMode.RefreshNow,
      showModeSelector: (allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
                         allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer ||
                         allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient),
      showRefreshNowTab: (allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer ||
                          allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient),
      showRefreshOnClientTab: false
    };

    scope.refreshNowPrompt = refreshNowPrompt;

    scope.remoteRefreshPropertiesEditModel = { refreshMode: RefreshModeTypes.Server, remoteAgentName: 'dummy' };

    scope.existingRefreshSchedulesCount = selectContentTotalLength(getAppStore().getState());

    // Set up remote refresh info when the mode is allowed.
    if ((allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
           allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
           allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient)
      && items.size() === 1) {
      const datasourceDetailInfo: IDataSourceDetailInfo = items.first() as IDataSourceDetailInfo;

      if (RefreshPropertyHelper.canBeRefreshedRemotely(datasourceDetailInfo)) {
        scope.dialog.showModeSelector = !(allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient);
        scope.dialog.showRefreshOnClientTab = true;

        scope.selfServiceSchedulePaneProps =
        this.getDefaultSelfServiceSchedulePanePropsForSSSWidget(datasourceDetailInfo.remoteAgentTimeZone);

        scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback = (newRecurrence: Recurrence) => {
          scope.$apply(() => {
            scope.selfServiceSchedulePaneProps = {
              recurrenceModel: newRecurrence,
              timeZoneDetails: scope.selfServiceSchedulePaneProps.timeZoneDetails,
              recurrenceModelChangedCallback: scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback,
              type: scope.selfServiceSchedulePaneProps.type,
            };
          });
        };

        scope.remoteRefreshPropertiesEditModel = { refreshMode: datasourceDetailInfo.refreshMode, remoteAgentName: datasourceDetailInfo.remoteAgentName };
        this.setRemoteAgentOptionsInScope(datasourceDetailInfo, scope);

        if (scope.remoteRefreshPropertiesEditModel.refreshMode === 'Remote' || allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient){
          scope.dialog.mode = DialogMode.ScheduleRefreshOnClient;
        }
      }
    }

    scope.existingRemoteRefreshPropertiesEditModel = {
      refreshMode: scope.remoteRefreshPropertiesEditModel.refreshMode,
      remoteAgentName: scope.remoteRefreshPropertiesEditModel.remoteAgentName
    };
  }

  private getDefaultSelfServiceSchedulePanePropsForSSSWidget(remoteAgentTimeZone: string): SelfServiceSchedulePaneProps {

      const scheduleDetails: ISelfServiceScheduleDetails = {
        startTime: 300,
        endTime: 0,
        recurrenceMinutes: 1440,
        recurrenceDaysOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
        recurrenceDayOfMonth: '0',
      };

      const selfServiceSchedulePaneProps = ChangeScheduleActionService.getSelfServiceSchedulePaneProps(
        scheduleDetails, remoteAgentTimeZone != null, remoteAgentTimeZone, 'Weekly',
        SelfServiceSchedulePaneType.BridgeExtractRefresh);

      return selfServiceSchedulePaneProps;
  }

  private setRemoteAgentOptionsInScope(datasource: IDataSourceDetailInfo, scope: ICreateExtractTasksScope) {
    return this.RemoteRefreshAgents.get()
      .then((result: IGetRemoteRefreshAgentsResult) => {
        const remoteAgents = ChangeDatasourceRemoteRefreshPropertiesAction.buildRemoteAgentsList(result, datasource);
        scope.remoteAgentOptions = remoteAgents;
        return scope;
      });
  }

  private execute<T extends IExtractItemInfo>(
    items: Set<T>,
    context: IActionContext,
    scheduleOnServerPrompt: string,
    scheduleOnClientPrompt: string,
    scheduleRefreshActionOnServer: (scope: ICreateExtractTasksScope, ids: string[], scheduleId: string, type: string) => ng.IPromise<IResult>,
    scheduleRefreshActionOnClient: (scope: ICreateExtractTasksScope, id: string, schedule: IRemoteRefreshScheduleInfo) => ng.IPromise<IResult>,
    refreshNowPrompt: string,
    refreshNowAction: (ids: string[], type: string) => ng.IPromise<IResult>,
    errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail,
    allowedMode: CreateExtractTasksMode
  ): ng.IPromise<IResult> {

    const scope = <ICreateExtractTasksScope> context.scope.$new();
    this.populateScope(scope, items, scheduleOnServerPrompt, scheduleOnClientPrompt, allowedMode, refreshNowPrompt);

    // Change the available buttons based on the dialog mode
    const buttons: IConfirmActionDialogButton[] = [];
    scope.$watch(() => scope.dialog.mode, () => {
      buttons.length = 0;
      if (scope.dialog.mode === DialogMode.ScheduleRefreshOnServer) {
        scope.remoteRefreshPropertiesEditModel.refreshMode = RefreshModeTypes.Server;

        buttons.push({
          label: items.size() === 1 ? this.$translate.instant('extractTaskCreateConfirm') : this.$translate.instant('extractTasksCreateConfirm'),
          action: () => scheduleRefreshActionOnServer(scope, items.keys(), scope.extractTask.schedule.id, scope.extractTask.type),
          disabled: () => !scope.extractTask.schedule || !scope.extractTask.type
        });
      } else if (scope.dialog.mode === DialogMode.ScheduleRefreshOnClient) {

        const recurrenceModel: Recurrence = scope.selfServiceSchedulePaneProps.recurrenceModel;
        const schedule: IRemoteRefreshScheduleInfo = {
          id: '1',
          contentType: 'remoteRefreshSchedule',
          definition: null,
          refreshType: scope.remoteRefreshTypeOption.refreshType,
          scheduleDetails: recurrenceModel.recurrenceDetails,
          scheduleType: recurrenceModel.recurrenceType
        };

        scope.remoteRefreshPropertiesEditModel.refreshMode = RefreshModeTypes.Remote;

        const buttonTranslationId = CreateExtractTasksMode.AllowScheduleOnlyOnClient ?
          'extractTaskCreateLegacyBridgeScheduleConfirm' : 'extractTaskCreateConfirm';
        buttons.push({
          label: this.$translate.instant(buttonTranslationId),
          action: () => scheduleRefreshActionOnClient(scope, '1', schedule),
          disabled: () => !scope.remoteAgentOptions ||
                          scope.remoteAgentOptions.length === 0 ||
                          !scope.remoteRefreshPropertiesEditModel ||
                          !scope.remoteRefreshPropertiesEditModel.remoteAgentName
        });
      } else if (scope.dialog.mode === DialogMode.RefreshNow) {
        buttons.push({
          label: this.$translate.instant('fullRefresh'),
          action: () => refreshNowAction(items.keys(), ExtractTaskType.RefreshExtract),
          disabled: () => !hasRunNowPermission(getAppStore().getState()),
        });
        if (scope.allowIncremental) {
          buttons.push({
            label: this.$translate.instant('incrementalRefresh'),
            action: () => refreshNowAction(items.keys(), ExtractTaskType.IncrementExtract),
            disabled: () => !hasRunNowPermission(getAppStore().getState()),
          });
        }
      } else {
        throw 'Unknown dialog mode: ' + scope.dialog.mode;
      }
    });

    scope.itemTextFn = (schedule: ISchedule) => {
      const description = this.scheduleFrequencyDescriptionFilter(schedule);
      if (!schedule.parallel) {
        return this.$translate.instant('serialScheduleDescription', {
          SERIAL_DESCRIPTION: description,
          SERIAL_DESCRIPTION_SUFFIX: this.$translate.instant('serialScheduleDescriptionSuffix')
        });
      }
      return description;
    };

    scope.displayScheduleDescriptionAsName = getSessionManagementServiceInstance().displayScheduleDescriptionAsName();

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: scope,
      position: { reserveHeight: 550 },
      bodyTemplateUrl: 'confirmActionDialogExtractRefresh.html',
      title: this.dialogTitle(allowedMode),
      buttons: buttons,
      size: ModalSize.large
    });

    dialogInstance.result
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          items,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractTasksCreateSuccess),
            error: standardActionErrorMsg(Messages.extractTasksCreateError),
          },
          (errorItem: IErrorItem<T>) => errorDetailFn(scope.extractTask.schedule)(errorItem)
        )
      ).finally(() => scope.$destroy());

    return dialogInstance.result;
  }

  private dialogTitle(allowedMode: CreateExtractTasksMode) {
    switch(allowedMode) {
      case CreateExtractTasksMode.AllowRefreshNowAndOnServer:
      case CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient:
        return this.$translate.instant('extractTasksDialogTitle_AllowBoth');
      case CreateExtractTasksMode.AllowScheduleOnlyOnClient:
        return this.$translate.instant('extractTasksDialogTitle_ScheduleLegacyBridgeRefresh');
      case CreateExtractTasksMode.AllowRefreshNow:
        return this.$translate.instant('extractTasksDialogTitle_AllowRefreshNow');
      case CreateExtractTasksMode.AllowScheduleOnServer:
      case CreateExtractTasksMode.AllowScheduleOnServerAndClient:
        return this.$translate.instant('extractTasksDialogTitle_AllowSchedule');
      default:
        throw 'Unknown allowed mode: ' + allowedMode;
    }
  }

  private createExtractErrorMessageFor(errorItem: IErrorItem<IExtractItemInfo>, schedule: ISchedule) {
    switch(errorItem.error.code) {
      case SchedulingErrorCode.SchedulingScheduleNotFound:
        return this.$translate.instant('scheduleNotFound', { SCHEDULE: schedule.name });
      case WebClientErrorCode.NotSupportedForRemotelyRefreshedContent:
        return this.$translate.instant('refreshNowRemotelyNotSupported');
    }
    return this.$translate.instant('errorUnknown');
  }

  // Workbook specific logic

  public forWorkbook(workbook: IWorkbookBase, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const workbookSet = Set.singleton(workbook, (wb: IWorkbookInfo) => wb.id);
    return this.forWorkbooks(workbookSet, context, allowedMode);
  }

  public forWorkbooks(workbooks: Set<IWorkbookBase>, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
    const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
    if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
      let angularPromise = this.$q.defer();
      RefreshSelfServiceScheduleDialog.open(getRefreshSSSDialogParams(workbooks.toArray())).then(result => angularPromise.resolve(result))
        .catch(error => angularPromise.reject(error));
      return angularPromise.promise;
    }
    const scheduleOnServerPrompt = (workbooks.size() === 1)
        ? this.$translate.instant('schedulesLabelPromptWorkbook', { WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name) })
        : this.$translate.instant('schedulesLabelPromptWorkbooks', { COUNT: workbooks.size() }, L10n.Interpolations.Count);
    const scheduleOnClientPrompt = '';

    const dummyScope = <ICreateExtractTasksScope> context.scope.$new();

    const refreshNowPrompt =
      this.$translate.instant('extractTasksRefreshNowOnWorkbooksPrompt', {
        WORKBOOK_COUNT: workbooks.size(),
        WORKBOOK_NAME: L10n.elideIfLong(workbooks.first().name)
      }, L10n.Interpolations.Count);

    return this.execute(
      workbooks,
      context,
      scheduleOnServerPrompt,
      scheduleOnClientPrompt,
      (dummyScope: ICreateExtractTasksScope, ids: string[], scheduleId: string, type: string) =>
        Workbooks.createExtractTask(ids, scheduleId, type),
      (dummyScope: ICreateExtractTasksScope, id: string, schedule: IRemoteRefreshScheduleInfo) =>
        { return null; },
      refreshNowPrompt,
      (ids: string[], type: string) => Workbooks.refreshExtracts(ids, type),
      (schedule: ISchedule) => this.workbookErrorDetailFactory(schedule),
      allowedMode
    );
  }

  public createExtractsForWorkbooks(workbook: INamedItemInfo, datasources: Set<IDataSourceBase>, context: IActionContext,
                  encryptionState: ExtractEncryptionState): ng.IPromise<IResult> {
    const scope = <ICreateExtractTasksScope> context.scope.$new();
    return Workbooks.createExtracts([].concat(workbook.id), datasources.keys(), encryptionState === ExtractEncryptionState.encrypted)
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          datasources,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractCreationTasksSuccess),
            error: standardActionErrorMsg(Messages.extractCreationTasksError),
          },
          (errorItem: IErrorItem<IDataSourceBase>) => this.datasourceErrorDetailFactory(null)(errorItem)
        )
      ).finally(() => scope.$destroy());
  }

  public createExtractsForDatasources(datasources: Set<IDataSourceBase>, context: IActionContext, encryptionState: ExtractEncryptionState): ng.IPromise<IResult> {
    const scope = <ICreateExtractTasksScope> context.scope.$new();
    return DataSources.createExtracts(datasources.keys(), encryptionState === ExtractEncryptionState.encrypted)
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          datasources,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractCreationTasksSuccess),
            error: standardActionErrorMsg(Messages.extractCreationTasksError),
          },
          (errorItem: IErrorItem<IDataSourceBase>) => this.datasourceErrorDetailFactory(null)(errorItem)
        )
      ).finally(() => scope.$destroy());
  }

  public pollExtractActionStatus(contentItem: IWorkbookInfo | IDataSourceInfo, actionType: string, result: IResult) {
    ActionHelpers.displayStandardToastsForResult(
      Set.singletonForIdItem(contentItem),
      result,
      this.messagesForExtractActionStatus(actionType),
      (errorItem: IErrorItem<IWorkbookBase>) => this.contentErrorDetailFactory(null)(errorItem)
    );
  }

  private messagesForExtractActionStatus(actionType: string): StandardActionToastMessages {
    if (actionType === ExtractActionType.createExtracts) {
      return {
        success: standardActionSuccessMsg(Messages.extractCreationJobSuccess),
        error: standardActionErrorMsg(Messages.extractCreationJobFailed),
      };
    } else if (actionType === ExtractActionType.encryptExtract) {
      return {
        success: standardActionSuccessMsg(Messages.extractEncryptionJobSuccess),
        error: standardActionErrorMsg(Messages.extractEncryptionJobFailed),
      };
    } else {
      return {
        success: standardActionSuccessMsg(Messages.extractDecryptionJobSuccess),
        error: standardActionErrorMsg(Messages.extractDecryptionJobFailed),
      };
    }
  }

  private workbookErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IWorkbookBase>): IErrorDetail => {
      return {
        itemName: errorItem.item.name,
        itemUrl: ContentUrlForItem(errorItem.item, WorkbookContentType),
        errorMessage: this.workbookErrorMessageFor(errorItem, schedule)
      };
    };
  }

  private workbookErrorMessageFor(errorItem: IErrorItem<IWorkbookBase>, schedule: ISchedule): string {
    switch(errorItem.error.code) {
      case WebClientErrorCode.TaskTypeRefreshExtractNotAllowed:
        return this.$translate.instant('extractTasksWorkbookNoExtract');
      case WebClientErrorCode.TaskTypeIncrementExtractNotAllowed:
        return this.$translate.instant('extractTasksWorkbookNoIncrementalExtract');
      case SchedulingErrorCode.SchedulingExtractTaskExistsForTarget:
        return this.$translate.instant('extractTasksRefreshAlreadyExistsForWorkbook', {
          SCHEDULE: schedule.name
        });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('extractTasksWorkbookErrorInsufficientPermissions');
      case AlertErrorCode.AlertConnectionlessFailure:
        return this.$translate.instant('credentialMissedOrWrong');
      case AlertErrorCode.AlertOperationCanceled:
        return this.$translate.instant('jobCancelled');
    }
    return this.createExtractErrorMessageFor(errorItem, schedule);
  }

  // Datasource specific logic

  public forDatasource(datasource: IDataSourceBase, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const datasourceSet = Set.singleton(datasource, (ds: IDataSourceBase) => ds.id);
    return this.forDatasources(datasourceSet, context, allowedMode);
  }

  public forDatasources(datasources: Set<IDataSourceBase>, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {

    if (!this.anyRefreshableDatasources(datasources)) {
      return this.showCannotRefreshErrorAlert(datasources);
    }
    const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
    const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
    const isCreateBridgeExtractRefresh = (allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient);
    if(isExtractSSSEnabledForServer && isExtractSSSEnabledForSite && !isCreateBridgeExtractRefresh) {
      let angularPromise = this.$q.defer();
      RefreshSelfServiceScheduleDialog.open(getRefreshSSSDialogParams(datasources.toArray())).then(result => angularPromise.resolve(result))
        .catch(error => angularPromise.reject(error));
      return angularPromise.promise;
    }

    const scheduleOnServerPrompt = (datasources.size() === 1)
      ? this.$translate.instant('schedulesLabelPromptDatasource', { DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name) })
      : this.$translate.instant('schedulesLabelPromptDatasources', { COUNT: datasources.size() }, L10n.Interpolations.Count);

    const scheduleOnClientPrompt = (datasources.size() === 1)
      ? this.$translate.instant('schedulesLabelPromptDatasourceOnClient', { DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name) })
      : this.$translate.instant('schedulesLabelPromptDatasources', { COUNT: datasources.size() }, L10n.Interpolations.Count);

    const refreshNowPrompt =
      this.$translate.instant('extractTasksRefreshNowOnDatasourcesPrompt', {
        DATASOURCE_COUNT: datasources.size(),
        DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name)
      }, L10n.Interpolations.Count);

    if ((allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
            allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
            allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient)
      && datasources.size() === 1) {
      const datasource: IDataSourceDetailInfo = datasources.first() as IDataSourceDetailInfo;
      const scheduleRefreshOnClientAction = (scope: ICreateExtractTasksScope, id: string, schedule: IRemoteRefreshScheduleInfo) => {
          const finalScheduleInfo: IRemoteRefreshScheduleInfo = {
            id: '',
            contentType: 'remoteRefreshSchedule',
            definition: '',
            refreshType: scope.remoteRefreshTypeOption.refreshType,
            scheduleDetails: scope.selfServiceSchedulePaneProps.recurrenceModel.recurrenceDetails,
            scheduleType: scope.selfServiceSchedulePaneProps.recurrenceModel.recurrenceType
          };

          // We should show success toasts from the change mode action if the requested refresh mode is Server,
          // because the change agent action does nothing, including showing a toast, if so.
          const showSuccessToast = scope.remoteRefreshPropertiesEditModel.refreshMode === RefreshModeTypes.Server;

          return ChangeDatasourceRefreshModeAction.execute(datasource, scope.remoteRefreshPropertiesEditModel.refreshMode, scope.remoteRefreshPropertiesEditModel.remoteAgentName, !showSuccessToast, false).then((result: any) => {
            return ChangeDatasourceRemoteAgentAction.execute(datasource, scope.remoteRefreshPropertiesEditModel.remoteAgentName, scope.remoteRefreshPropertiesEditModel.refreshMode, false).then((result: any) => {
              return RemoteRefreshSchedules.create(datasource.id, finalScheduleInfo).finally(() => {
                scope.$destroy();
                getAppStore().dispatch(incrementRouterSpaceReactKey());
              });
            });
          });
      };

      const scheduleRefreshOnServerAction = (scope: ICreateExtractTasksScope, ids: string[], scheduleId: string, type: string) => {
        const showSuccessToast = scope.remoteRefreshPropertiesEditModel.refreshMode === RefreshModeTypes.Server;

        if (datasource.usingRemoteQueryAgent) {
          return DataSources.createExtractTask(ids, scheduleId, type).then((result: any) => {
            return ChangeDatasourceRefreshModeAction.execute(datasource, scope.remoteRefreshPropertiesEditModel.refreshMode, scope.remoteRefreshPropertiesEditModel.remoteAgentName, !showSuccessToast, false).finally(() => {
              scope.$destroy();
              getAppStore().dispatch(incrementRouterSpaceReactKey());
            });
          });
        } else {
          return DataSources.setUsingRemoteQueryAgent(datasource.id).then((result: any) => {
            return DataSources.createExtractTask(ids, scheduleId, type).then((result: any) => {
              return ChangeDatasourceRefreshModeAction.execute(datasource, scope.remoteRefreshPropertiesEditModel.refreshMode, scope.remoteRefreshPropertiesEditModel.remoteAgentName, !showSuccessToast, false).finally(() => {
                scope.$destroy();
                getAppStore().dispatch(incrementRouterSpaceReactKey());
              });
            });
          });
        }
      };

      return this.execute(
        datasources,
        context,
        scheduleOnServerPrompt,
        scheduleOnClientPrompt,
        scheduleRefreshOnServerAction,
        scheduleRefreshOnClientAction,
        refreshNowPrompt,
        (ids: string[], type: string) => DataSources.refreshExtracts(ids, type),
        (schedule: ISchedule) => this.datasourceErrorDetailFactory(schedule),
        allowedMode
      );
    } else { // Keep the old behavior

      const scheduleRefreshOnServerAction = (scope: ICreateExtractTasksScope, ids: string[], scheduleId: string, type: string) => {
        return DataSources.createExtractTask(ids, scheduleId, type);
      };

      return this.execute(
        datasources,
        context,
        scheduleOnServerPrompt,
        scheduleOnClientPrompt,
        scheduleRefreshOnServerAction,
        (dummyScope: ICreateExtractTasksScope, id: string, schedule: IRemoteRefreshScheduleInfo) => { return null; },
        refreshNowPrompt,
        (ids: string[], type: string) => DataSources.refreshExtracts(ids, type),
        (schedule: ISchedule) => this.datasourceErrorDetailFactory(schedule),
        allowedMode
      );
    }
  }

  private anyRefreshableDatasources(datasources: Set<IDataSourceBase>) {
    return _.any(datasources.toArray(), (ds: IDataSourceBase) => this.isRefreshableDatasource(ds));
  }

  public isRefreshableDatasource(datasource: IDataSourceBase) {
    // To be refreshable, a datasource must be:
    //   1. Not embedded in a workbook
    //   2. Have an extract

    // Embedded datasources are not refreshable
    if (_.isObject(datasource.workbook)) {
      return false;
    }

    // Live datasources are not refreshable
    if (!datasource.hasExtracts) {
      return false;
    }

    return true;
  }

  private showCannotRefreshErrorAlert(datasources: Set<IDataSourceBase>) {

    // Determine which message to display

    const allEmbedded = datasources.all((ds: IDataSourceBase) => _.isObject(ds.workbook));
    const anyEmbedded = datasources.any((ds: IDataSourceBase) => _.isObject(ds.workbook));

    let message = Messages.refreshExtracts_cannotRefreshAlertMessage_allLive();
    if (allEmbedded) {
      message = Messages.refreshExtracts_cannotRefreshAlertMessage_allEmbedded();
    } else if (anyEmbedded) {
      message = Messages.refreshExtracts_cannotRefreshAlertMessage_mixedEmbeddedAndLive();
    }

    return openReactAlertDialog({
      title: Messages.refreshExtracts_cannotRefreshAlertTitle(),
      message
    }).result;
  }

  private datasourceErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IDataSourceBase>): IErrorDetail => {
      return {
        itemName: errorItem.item.name,
        itemUrl: ContentUrlForItem(errorItem.item, DatasourceContentType),
        errorMessage: this.datasourceErrorMessageFor(errorItem, schedule)
      };
    };
  }

  private datasourceErrorMessageFor(errorItem: IErrorItem<IDataSourceBase>, schedule: ISchedule): string {
    switch(errorItem.error.code) {
      case WebClientErrorCode.TaskTypeRefreshExtractNotAllowed:
        return this.$translate.instant('extractTasksDatasourceNoExtract');
      case WebClientErrorCode.TaskTypeIncrementExtractNotAllowed:
        return this.$translate.instant('extractTasksDatasourceNoIncrementalExtract');
      case SchedulingErrorCode.SchedulingExtractTaskExistsForTarget:
        return this.$translate.instant('extractTasksRefreshAlreadyExistsForDatasource', {
          SCHEDULE: schedule.name
        });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return this.$translate.instant('extractTasksDatasourceErrorInsufficientPermissions');
      case UnifiedDatasourceErrorCode.OperationOnlySupportedForPublishedDatasource:
        return this.$translate.instant('errorOperationOnlySupportedForPublishedDatasource');
      case RemoteAgentErrorCode.NoAgentAssignedToDataSource:
        return this.$translate.instant('extractTasksDatasourceErrorRemoteAgentNotAssigned');
      case RemoteAgentErrorCode.AgentNotConnected:
        return this.$translate.instant('extractTasksDatasourceErrorRemoteAgentNotConnected');
      case RemoteAgentErrorCode.AgentNeedsUpdateToSupportFeature:
        return this.$translate.instant('extractTasksDatasourceErrorRemoteAgentNeedsUpdate');
      case AlertErrorCode.AlertSignInFailed:
        return this.$translate.instant('credentialMissedOrWrong');
      case AlertErrorCode.AlertOperationCanceled:
        return this.$translate.instant('jobCancelled');
    }
    return this.createExtractErrorMessageFor(errorItem, schedule);
  }

  public forContent(content: Set<IContentInfo>, context: IActionContext, allowedMode: CreateExtractTasksMode): ng.IPromise<IResult> {
    const workbooksAndDatasources: Set<ExtractType> = new Set(Set.PluckId);
    const ValidTypes = Set.fromKeyArray(['workbook', 'datasource']);
    content.each(item => {
      if (ValidTypes.containsKey(item.contentType)) {
        workbooksAndDatasources.add(item as ExtractType);
      }
    });

    const contentCount = workbooksAndDatasources.size();
    const firstItemName = L10n.elideIfLong(workbooksAndDatasources.first().name);
    const scheduleOnServerPrompt = this.$translate.instant('schedulesLabelPromptContentItems', {
        CONTENT_COUNT: contentCount,
        CONTENT_NAME: firstItemName
      }, L10n.Interpolations.Count);
    const scheduleOnClientPrompt = '';

    const dummyScope = <ICreateExtractTasksScope> context.scope.$new();

    const refreshNowPrompt = this.$translate.instant('extractTasksRefreshNowOnContentPrompt', {
        CONTENT_COUNT: contentCount,
        CONTENT_NAME: L10n.elideIfLong(firstItemName)
      }, L10n.Interpolations.Count);

    return this.execute(
      workbooksAndDatasources.clone(),
      context,
      scheduleOnServerPrompt,
      scheduleOnClientPrompt,
      (dummyScope: ICreateExtractTasksScope, ids: string[], scheduleId: string, type: string) =>
        this.createExtractTasksForContent(workbooksAndDatasources, scheduleId, type),
      (dummyScope: ICreateExtractTasksScope, id: string, schedule: IRemoteRefreshScheduleInfo) =>
        { return null; },
      refreshNowPrompt,
      (ids: string[], type: string) => this.refreshExtractsForContent(workbooksAndDatasources, type),
      (schedule: ISchedule) => this.contentErrorDetailFactory(schedule),
      allowedMode
    );
  }

  private createExtractTasksForContent = (content: Set<ExtractType>, scheduleId: string, type: string): ng.IPromise<IResult> => {
    const workbooksPromise = Workbooks.createExtractTask(content.filter(et => 'workbook' === et.contentType).map(w => w.id), scheduleId, type);
    const datasourcesPromise = DataSources.createExtractTask(content.filter(et => 'datasource' === et.contentType).map(d => d.id), scheduleId, type);

    return this.$q.all([workbooksPromise, datasourcesPromise]).then((resultsArray: IResult[]) => {
      return {
        errors: [...resultsArray[0].errors, ...resultsArray[1].errors]
      };
    });
  }

  private refreshExtractsForContent = (content: Set<IContentInfo>, type: string): ng.IPromise<IResult> => {
    const workbooksPromise = Workbooks.refreshExtracts(content.filter(et => 'workbook' === et.contentType).map(w => w.id), type);
    const datasourcesPromise = DataSources.refreshExtracts(content.filter(et => 'datasource' === et.contentType).map(d => d.id), type);

    return this.$q.all([workbooksPromise, datasourcesPromise]).then((resultsArray: IResult[]) => {
      return {
        errors: [...resultsArray[0].errors, ...resultsArray[1].errors]
      };
    });
  }

  private contentErrorDetailFactory = (schedule: ISchedule): (errorItem: IErrorItem<ExtractType>) => IErrorDetail => {
    const workbookErrorDetailFactory = this.workbookErrorDetailFactory(schedule);
    const datasourceErrorDetailFactory = this.datasourceErrorDetailFactory(schedule);

    return (errorItem: IErrorItem<ExtractType>): IErrorDetail => {
      return 'workbook' === errorItem.item.contentType ?
        workbookErrorDetailFactory(errorItem as IErrorItem<IWorkbookBase>) :
        datasourceErrorDetailFactory(errorItem as IErrorItem<IDataSourceBase>);
    };
  }

}

Module.instance().service('CreateExtractTasksAction', CreateExtractTasksAction);
